import { createApi } from 'ReduxHelpers';
import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { statusOptionsWithChildren } from 'AdminUsers/StatusOptions';
import { LISTED_TOP_LEVEL_STATUSES } from './constants';
import {
    type BatchUpdateArgs,
    type BatchUpdateResponse,
    type EnrollmentStatusStudent,
    type StudentsIndexResponse,
    type StudentsQueryArgs,
} from './AdminCohortEnrollmentStatus.types';

/*
    The users index is a POST, because the filters are too long for a query string. See
    overrideAction('index', { method: 'POST', path: 'index' }) on the Iguana User model, and
    `post '/api/users/index'` in config/routes/api_routes.rb.

    The base query snake-cases the request body all the way down, so `filters.programRows`
    arrives as `filters[program_rows]`, and camel-cases the response the same way.
*/

// The whole cohort comes back in one response, and the table pages it in the browser.
const ALL_RECORDS = Number.MAX_SAFE_INTEGER;

/*
    The shared base query retries a disconnect and a 502/504. user_id_verifications has a unique
    index on (user_id, cohort_id, id_verification_period_index) and no model-level validation, so
    retrying a PUT that Rails already committed would raise instead of saving.
*/
const NO_RETRY = { maxRetries: 0 };

export const adminEnrollmentStatusApi = createApi('adminEnrollmentStatusApi', {
    ...getBackRoyalApiBaseConfig('adminEnrollmentStatusApi'),
    endpoints: builder => ({
        getEnrollmentStatusStudents: builder.query<EnrollmentStatusStudent[], StudentsQueryArgs>({
            query: ({ cohortId }) => ({
                method: 'POST',
                url: 'users/index.json',
                body: {
                    view: 'cohort_enrollment_status',
                    perPage: ALL_RECORDS,
                    filters: {
                        programRows: [
                            {
                                cohortId,
                                status: statusOptionsWithChildren([...LISTED_TOP_LEVEL_STATUSES]),
                            },
                        ],
                    },
                },
            }),
            transformResponse: (response: StudentsIndexResponse) => response.contents.users || [],
        }),

        batchUpdateEnrollmentStatus: builder.mutation<BatchUpdateResponse['contents']['users'], BatchUpdateArgs>({
            query: ({ cohortId, records }) => ({
                method: 'PUT',
                url: 'users/batch_update_cohort_enrollment_status.json',
                body: { records, meta: { cohortId } },
            }),
            transformResponse: (response: BatchUpdateResponse) => response.contents.users || [],
            extraOptions: NO_RETRY,
        }),
    }),
});
